// RecordPaymentModal — the office's "Payment Received" form, modelled on
// Zoho Books' own (owner, 2026-09-15). One amount, bank transfer by default
// (cash selectable), applied to the client's pending invoices oldest-first
// with every line editable; whatever is not applied is credited to the
// wallet as excess. Managers only — the button is only rendered for them and
// the route refuses everyone else.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { formatDubai, todayDubaiYMD } from '../../../utils/dubaiTime';
import { allocateOldestFirst, round2 } from './recordPaymentAlloc';
import './recordPayment.css';

const MODE_OPTIONS = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
];

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderRadius: 8,
        borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#2563eb' : '#d1d5db' },
        fontSize: 14,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
};

const money = (n) => `AED ${round2(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RecordPaymentModal({ clientId, pendingInvoices = [], isMulti = false, wallets = [], onClose, onRecorded }) {
    const invoices = useMemo(() => (
        [...pendingInvoices]
            .filter((inv) => Number(inv.balance) > 0.009)
            .sort((a, b) => (new Date(a.date) - new Date(b.date)) || String(a.invoice_number).localeCompare(String(b.invoice_number)))
    ), [pendingInvoices]);

    const [amount, setAmount] = useState('');
    const [mode, setMode] = useState(MODE_OPTIONS[0]);
    // Separate entity wallets: where the excess goes. Only offered when the client has more than one wallet.
    const walletOptions = useMemo(() => wallets.map((w) => ({ value: w.id, label: w.label })), [wallets]);
    const [excessWallet, setExcessWallet] = useState(null);
    const [date, setDate] = useState(todayDubaiYMD());
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    // Per-invoice payment inputs (strings, so a half-typed "12." survives).
    const [lines, setLines] = useState({});
    const [manual, setManual] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const amountNum = round2(amount);

    // Standard prefill: oldest invoice first, re-done whenever the amount
    // changes — until the office edits a line by hand.
    useEffect(() => {
        if (manual) return;
        const alloc = allocateOldestFirst(amountNum, invoices);
        setLines(Object.fromEntries(Object.entries(alloc).map(([k, v]) => [k, v > 0 ? v.toFixed(2) : ''])));
    }, [amountNum, invoices, manual]);

    const applied = useMemo(() => invoices.map((inv) => ({
        inv,
        value: round2(lines[inv.zoho_invoice_id]),
        over: round2(lines[inv.zoho_invoice_id]) > round2(inv.balance) + 0.009,
    })), [invoices, lines]);
    const appliedTotal = round2(applied.reduce((s, a) => s + (a.value > 0 ? a.value : 0), 0));
    const excess = round2(amountNum - appliedTotal);
    const overApplied = excess < -0.009;
    const anyOver = applied.some((a) => a.over);
    const appliedCount = applied.filter((a) => a.value > 0.009).length;
    const dateInFuture = date && date > todayDubaiYMD();
    const canSubmit = amountNum > 0.009 && !overApplied && !anyOver && !dateInFuture && !saving;

    const setLine = (id, value) => {
        setManual(true);
        setLines((prev) => ({ ...prev, [id]: value }));
    };
    const resetToOldestFirst = () => { setManual(false); };
    // One click for the common case: the client paid everything that is open.
    const pendingTotal = round2(invoices.reduce((s, inv) => s + round2(inv.balance), 0));
    const useTotalPending = () => { setManual(false); setAmount(pendingTotal.toFixed(2)); };
    const payInFull = (inv) => setLine(inv.zoho_invoice_id, round2(inv.balance).toFixed(2));

    const submit = async () => {
        if (!canSubmit) return;
        setSaving(true);
        setError('');
        try {
            const res = await axios.post(`/api/clients/${clientId}/payments`, {
                amount: amountNum,
                payment_mode: mode.value,
                excess_wallet_client_id: excessWallet?.value || null,
                date,
                reference: reference.trim() || null,
                notes: notes.trim() || null,
                applications: applied
                    .filter((a) => a.value > 0.009)
                    .map((a) => ({ zoho_invoice_id: a.inv.zoho_invoice_id, amount: a.value })),
            });
            const r = res.data || {};
            const parts = [];
            if (r.applied_total > 0.009) parts.push(`${money(r.applied_total)} applied to ${r.payments.reduce((n, p) => n + p.invoices.length, 0)} invoice${r.payments.reduce((n, p) => n + p.invoices.length, 0) === 1 ? '' : 's'}`);
            if (r.excess > 0.009) parts.push(`${money(r.excess)} credited to the wallet`);
            toast.success(`${money(r.amount)} ${mode.label.toLowerCase()} recorded — ${parts.join(', ') || 'nothing applied'}.`);
            if (onRecorded) onRecorded(r);
            onClose();
        } catch (e) {
            const data = e.response?.data || {};
            let msg = data.error || 'Could not record the payment.';
            if (Array.isArray(data.created) && data.created.length) {
                msg += ` Already recorded in Zoho: ${data.created.map((c) => `${money(c.amount)}${c.entity_label ? ` on ${c.entity_label}` : ''}`).join(', ')} — do not record those again.`;
            }
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rpay__overlay" onClick={() => !saving && onClose()}>
            <div className="rpay__modal" role="dialog" aria-modal="true" aria-labelledby="rpay-title" onClick={(e) => e.stopPropagation()}>
                <div className="rpay__head">
                    <div>
                        <h3 id="rpay-title" className="rpay__title">Payment Received</h3>
                        <p className="rpay__subtitle">
                            Recorded in Zoho Books and applied to the invoices below. Anything left over is credited to the wallet.
                        </p>
                    </div>
                    <button type="button" className="rpay__close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
                </div>

                <div className="rpay__grid">
                    <div className="rpay__field">
                        <div className="rpay__labelRow">
                            <label className="rpay__label" htmlFor="rpay-amount">Amount received (AED)</label>
                            {pendingTotal > 0.009 && (
                                <button
                                    type="button"
                                    className={`rpay__quick${Math.abs(amountNum - pendingTotal) < 0.005 ? ' rpay__quick--on' : ''}`}
                                    onClick={useTotalPending}
                                    disabled={saving}
                                    title="Fill in everything the client has pending"
                                >
                                    Total pending · {money(pendingTotal)}
                                </button>
                            )}
                        </div>
                        <input
                            id="rpay-amount"
                            className="rpay__input rpay__input--amount"
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            disabled={saving}
                            autoFocus
                        />
                    </div>
                    <div className="rpay__field">
                        <label className="rpay__label" htmlFor="rpay-date">Payment date</label>
                        <input
                            id="rpay-date"
                            className="rpay__input"
                            type="date"
                            max={todayDubaiYMD()}
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            disabled={saving}
                        />
                    </div>
                    <div className="rpay__field">
                        <label className="rpay__label">Payment mode</label>
                        <Select
                            classNamePrefix="rpay-sel"
                            options={MODE_OPTIONS}
                            value={mode}
                            onChange={(opt) => setMode(opt || MODE_OPTIONS[0])}
                            isSearchable={false}
                            isDisabled={saving}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    <div className="rpay__field">
                        <label className="rpay__label" htmlFor="rpay-ref">Reference #</label>
                        <input
                            id="rpay-ref"
                            className="rpay__input"
                            type="text"
                            maxLength={49}
                            placeholder={mode.value === 'cash' ? 'Optional' : 'Bank reference (optional)'}
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            disabled={saving}
                        />
                    </div>
                    {walletOptions.length > 1 && (
                        <div className="rpay__field rpay__field--full">
                            <label className="rpay__label">Excess goes to</label>
                            <Select
                                classNamePrefix="rpay-sel"
                                options={walletOptions}
                                value={excessWallet || walletOptions[0]}
                                onChange={(opt) => setExcessWallet(opt)}
                                isSearchable={false}
                                isDisabled={saving}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    )}
                    <div className="rpay__field rpay__field--full">
                        <label className="rpay__label" htmlFor="rpay-notes">Notes</label>
                        <input
                            id="rpay-notes"
                            className="rpay__input"
                            type="text"
                            maxLength={500}
                            placeholder="Optional — shown on the Zoho payment"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={saving}
                        />
                    </div>
                </div>

                <div className="rpay__sectionHead">
                    <span className="rpay__sectionTitle">Apply to invoices ({invoices.length})</span>
                    {invoices.length > 0 && (
                        manual ? (
                            <button type="button" className="rpay__link" onClick={resetToOldestFirst} disabled={saving}>
                                Reset to oldest first
                            </button>
                        ) : (
                            <span className="rpay__sectionHint">Prefilled oldest first — edit any line</span>
                        )
                    )}
                </div>

                {invoices.length === 0 ? (
                    <div className="rpay__empty">No pending invoices — the whole amount will be credited to the wallet.</div>
                ) : (
                    <div className="rpay__tableWrap">
                        <table className="rpay__table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Invoice</th>
                                    {isMulti && <th>Billing entity</th>}
                                    <th className="rpay__num">Invoice amount</th>
                                    <th className="rpay__num">Amount due</th>
                                    <th className="rpay__num rpay__payCol">Payment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {applied.map(({ inv, value, over }) => (
                                    <tr key={inv.zoho_invoice_id} className={value > 0.009 ? 'rpay__row--active' : undefined}>
                                        <td className="rpay__date">{formatDubai(inv.date, 'd MMM yyyy') || '—'}</td>
                                        <td className="rpay__invoice">{inv.invoice_number || inv.id}</td>
                                        {isMulti && <td className="rpay__entity">{inv.entity_label || '—'}</td>}
                                        <td className="rpay__num">{money(inv.amount)}</td>
                                        <td className="rpay__num">
                                            <button
                                                type="button"
                                                className="rpay__due"
                                                title="Pay this invoice in full"
                                                onClick={() => payInFull(inv)}
                                                disabled={saving}
                                            >
                                                {money(inv.balance)}
                                            </button>
                                        </td>
                                        <td className="rpay__num rpay__payCol">
                                            <input
                                                className={`rpay__input rpay__lineInput${over ? ' rpay__lineInput--over' : ''}`}
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                inputMode="decimal"
                                                placeholder="0.00"
                                                value={lines[inv.zoho_invoice_id] ?? ''}
                                                onChange={(e) => setLine(inv.zoho_invoice_id, e.target.value)}
                                                disabled={saving}
                                                aria-label={`Payment for invoice ${inv.invoice_number || inv.id}`}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="rpay__totals">
                    <div className="rpay__totalRow">
                        <span>Amount received</span>
                        <strong>{money(amountNum)}</strong>
                    </div>
                    <div className="rpay__totalRow">
                        <span>Amount used for payments{appliedCount ? ` (${appliedCount} invoice${appliedCount === 1 ? '' : 's'})` : ''}</span>
                        <strong className={overApplied ? 'rpay__bad' : undefined}>{money(appliedTotal)}</strong>
                    </div>
                    <div className={`rpay__totalRow rpay__totalRow--excess${overApplied ? ' rpay__totalRow--bad' : ''}`}>
                        <span>{overApplied ? 'Over-applied by' : 'Amount in excess → credited to wallet'}</span>
                        <strong>{money(Math.abs(excess))}</strong>
                    </div>
                </div>

                {(error || overApplied || anyOver || dateInFuture) && (
                    <div className="rpay__error">
                        {error
                            || (dateInFuture && 'The payment date cannot be in the future.')
                            || (anyOver && 'A payment line is more than that invoice’s amount due.')
                            || 'The invoice payments add up to more than the amount received.'}
                    </div>
                )}

                <div className="rpay__actions">
                    <button type="button" className="rpay__btn rpay__btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="button" className="rpay__btn rpay__btn--primary" onClick={submit} disabled={!canSubmit}>
                        {saving ? 'Recording…' : (amountNum > 0.009 ? `Record ${money(amountNum)}` : 'Record payment')}
                    </button>
                </div>
            </div>
        </div>
    );
}
